// The holographic globe stage every globe screen draws on: scene, orbit camera rig, globe and post pipeline, with
// viewport, quality and look (colours and brightness, globeLook.ts) handling. Screens add their layers to `scene` and
// call update() then render() per frame.

import { Scene, type WebGLRenderer } from 'three'
import { GlobeCameraRig } from '../../render/camera/GlobeCameraRig'
import type { GlobeGeo } from '../../render/geo/polylines'
import { GlobeRenderer } from '../../render/globe/GlobeRenderer'
import type { GlobeLook } from '../../render/globe/globeLook'
import { PostPipeline } from '../../render/post/PostPipeline'
import type { Viewport } from '../../render/viewport'
import { QUALITY_PROFILES, type VisualSettings } from '../../render/visualSettings'
import type { ScreenContext } from '../screen'

export class GlobeStage {
  readonly scene = new Scene()
  readonly rig: GlobeCameraRig

  private readonly renderer: WebGLRenderer
  private readonly globe: GlobeRenderer
  private post: PostPipeline
  private settings: VisualSettings
  private viewport: Viewport
  private look: GlobeLook

  constructor(context: ScreenContext, geo: GlobeGeo, look: GlobeLook) {
    this.renderer = context.renderer
    this.settings = context.settings
    this.viewport = context.viewport
    this.look = look
    const profile = QUALITY_PROFILES[this.settings.quality]
    this.rig = new GlobeCameraRig(this.renderer.domElement)
    this.globe = new GlobeRenderer(geo, profile, look)
    this.scene.add(this.globe.root)
    this.post = new PostPipeline(this.renderer, this.scene, this.rig.camera, profile, this.viewport, this.settings.reduceEffects, look.palette.background)
    this.applyViewport()
  }

  resize(viewport: Viewport): void {
    this.viewport = viewport
    this.applyViewport()
    this.post.setSize(viewport)
  }

  /** Quality (globe density, post targets) and "Reduce effects" on the post pipeline. */
  applySettings(settings: VisualSettings): void {
    const previous = this.settings
    this.settings = settings
    if (settings.quality !== previous.quality) {
      const profile = QUALITY_PROFILES[settings.quality]
      this.globe.setQuality(profile)
      // MSAA sample counts are fixed per render target, so a new tier needs a new pipeline.
      const post = new PostPipeline(this.renderer, this.scene, this.rig.camera, profile, this.viewport, settings.reduceEffects, this.look.palette.background)
      this.post.dispose()
      this.post = post
    }
    this.post.setReduceEffects(settings.reduceEffects)
  }

  /** The globe's colours and brightness, and space behind it (a retune or palette change). */
  setLook(look: GlobeLook): void {
    this.look = look
    this.globe.setLook(look)
    this.post.setBackground(look.palette.background)
  }

  /** Camera and globe animation; call before anything reads the camera this frame. */
  update(dtSeconds: number): void {
    this.rig.update(dtSeconds)
    this.globe.update(dtSeconds)
  }

  render(dtSeconds: number): void {
    this.post.render(dtSeconds)
  }

  dispose(): void {
    this.post.dispose()
    this.globe.dispose()
    this.rig.dispose()
  }

  private applyViewport(): void {
    this.rig.setViewport(this.viewport)
    this.globe.setProjectionScale(this.rig.pixelsPerUnitAtUnitDepth(this.viewport))
  }
}
